import { useCallback, useEffect, useRef, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import GroupsIcon from '@mui/icons-material/Groups';
import { Box, IconButton, Stack, styled, Tooltip, Typography } from '@mui/material';
import { ReactComponent as GasanLayoutSVG } from 'src/assets/organizationLayoutImage/divisionOffice/fullLayout.svg';
import { ReactComponent as FullLayoutSVG } from 'src/assets/organizationLayoutImage/fullLayout.svg';

import { EMPTY_SEAT_COLOR, getTeamColorsByDepartment } from '../constants/teamColors';
import { OfficeType, SeatInfo } from '../types/seatTypes';
import { generateSeatId } from '../utils/seatChartParser';

interface SeatMapProps {
  officeType: OfficeType;
  seats: SeatInfo[];
  editMode: boolean;
  onSeatClick?: (seatId: string, x: number, y: number, width: number, height: number) => void;
  onSeatDoubleClick?: (seat: SeatInfo) => void;
}

const SeatMap = ({ officeType, seats, editMode, onSeatClick, onSeatDoubleClick }: SeatMapProps) => {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [showLegend, setShowLegend] = useState(true);
  const teamColorsByDepartment = getTeamColorsByDepartment();
  const [lastClickTime, setLastClickTime] = useState<number>(0);
  const [lastClickSeatId, setLastClickSeatId] = useState<string>('');

  /**
   * SVG 내 모든 좌석 rect에 data-seat-id 속성 설정
   * 본사/가산 모두 *-desks 그룹 기반으로 동작
   */
  const initializeSeatIds = useCallback(() => {
    const svg = svgRef.current;
    if (!svg) return;

    // *-desks 그룹 내의 rect에 seat-id 설정
    const deskGroups = svg.querySelectorAll('[id$="-desks"]');
    deskGroups.forEach((group) => {
      const rects = group.querySelectorAll('rect');
      rects.forEach((rect) => {
        const x = parseFloat(rect.getAttribute('x') || '0');
        const y = parseFloat(rect.getAttribute('y') || '0');
        const seatId = generateSeatId(x, y);
        rect.setAttribute('data-seat-id', seatId);
      });
    });
  }, []);

  /**
   * 좌석에 정보 렌더링
   */
  const applySeatInfo = useCallback((seat: SeatInfo) => {
    const svg = svgRef.current;
    if (!svg) return;

    const seatRect = svg.querySelector(`[data-seat-id="${seat.seatId}"]`) as SVGRectElement;
    if (!seatRect) return;

    // 공석 여부 확인
    const isEmpty = !seat.employeeId || seat.employeeId === 0;

    // 색상 적용 (공석인 경우 회색)
    const fillColor = isEmpty ? EMPTY_SEAT_COLOR : seat.teamColor || EMPTY_SEAT_COLOR;
    seatRect.style.setProperty('fill', fillColor, 'important');

    // 기존 텍스트 제거
    const existingGroup = svg.querySelector(`[data-for-seat="${seat.seatId}"]`);
    if (existingGroup) {
      existingGroup.remove();
    }

    const rect = seatRect.getBBox();
    const textGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    textGroup.setAttribute('data-for-seat', seat.seatId);

    if (seat.employeeName && !isEmpty) {
      // 직원 정보 표시
      const nameText = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      nameText.setAttribute('x', String(rect.x + rect.width / 2));
      nameText.setAttribute('y', String(rect.y + rect.height / 2 - 3));
      nameText.setAttribute('text-anchor', 'middle');
      nameText.setAttribute('font-size', '9');
      nameText.setAttribute('font-weight', 'bold');
      nameText.setAttribute('fill', '#231815');
      nameText.setAttribute('pointer-events', 'none');
      nameText.textContent = seat.employeeName;
      textGroup.appendChild(nameText);

      if (seat.employeePosition) {
        const posText = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        posText.setAttribute('x', String(rect.x + rect.width / 2));
        posText.setAttribute('y', String(rect.y + rect.height / 2 + 6));
        posText.setAttribute('text-anchor', 'middle');
        posText.setAttribute('font-size', '7');
        posText.setAttribute('fill', '#666');
        posText.setAttribute('pointer-events', 'none');
        posText.textContent = seat.employeePosition;
        textGroup.appendChild(posText);
      }
    }

    svg.appendChild(textGroup);
  }, []);

  /**
   * SVG 로드 후 좌석 ID 초기화 및 데이터 적용
   */
  useEffect(() => {
    // SVG 로딩 대기 후 초기화
    const timer = setTimeout(() => {
      initializeSeatIds();
      // 저장된 좌석 데이터 적용
      seats.forEach((seat) => applySeatInfo(seat));
    }, 100);
    return () => clearTimeout(timer);
  }, [officeType, seats, initializeSeatIds, applySeatInfo]);

  /**
   * rect가 선택 가능한지 확인
   * 본사/가산 모두 *-desks 그룹 기반으로 동작
   */
  const isSelectableRect = useCallback((target: SVGElement): boolean => {
    // *-desks 그룹 내의 rect
    const deskGroup = target.closest('[id$="-desks"]');
    return !!deskGroup;
  }, []);

  /**
   * SVG 클릭 핸들러
   */
  const handleSvgClick = (e: React.MouseEvent<SVGSVGElement>) => {
    const target = e.target as SVGElement;

    if (target.tagName === 'rect' && isSelectableRect(target)) {
      e.stopPropagation();

      const x = parseFloat(target.getAttribute('x') || '0');
      const y = parseFloat(target.getAttribute('y') || '0');
      const width = parseFloat(target.getAttribute('width') || '0');
      const height = parseFloat(target.getAttribute('height') || '0');

      const seatId = generateSeatId(x, y);
      target.setAttribute('data-seat-id', seatId);

      // editMode일 때는 단일 클릭으로 편집 모달 열기
      if (editMode && onSeatClick) {
        onSeatClick(seatId, x, y, width, height);
        return;
      }

      // editMode가 아닐 때는 더블클릭으로 직원 정보 모달 열기
      if (!editMode && onSeatDoubleClick) {
        const now = Date.now();
        const DOUBLE_CLICK_DELAY = 350;

        // 같은 좌석을 더블클릭한 경우
        if (lastClickSeatId === seatId && now - lastClickTime < DOUBLE_CLICK_DELAY) {
          const seatInfo = seats.find((s) => s.seatId === seatId);
          if (seatInfo && seatInfo.employeeId && seatInfo.employeeId > 0) {
            onSeatDoubleClick(seatInfo);
          }
          setLastClickTime(0);
          setLastClickSeatId('');
        } else {
          setLastClickTime(now);
          setLastClickSeatId(seatId);
        }
      }
    }
  };

  const LayoutSVG = officeType === 'main' ? FullLayoutSVG : GasanLayoutSVG;

  return (
    <MapContainer>
      {/* 수정모드 안내 */}
      {editMode && (
        <EditModeNotice>
          <Typography variant="body2" color="primary">
            수정 모드: 좌석을 클릭하여 직원을 배치하세요
          </Typography>
        </EditModeNotice>
      )}

      {/* 팀 색상 범례 */}
      <>
        {/* 범례 토글 버튼 */}
        <LegendToggleButton>
          <Tooltip title={showLegend ? '팀 안내 숨기기' : '팀 안내'}>
            <IconButton size="small" onClick={() => setShowLegend(!showLegend)}>
              {showLegend ? (
                <CloseIcon fontSize="small" />
              ) : (
                <>
                  <GroupsIcon fontSize="small" />
                  {'  '}
                  <Typography variant="caption" color="text.secondary" fontWeight="medium">
                    팀 안내
                  </Typography>
                </>
              )}
            </IconButton>
          </Tooltip>
        </LegendToggleButton>

        {/* 범례 내용 */}
        {showLegend && (
          <TeamColorLegend>
            <LegendGrid>
              {Object.entries(teamColorsByDepartment).map(([department, teams]) => (
                <Box key={department}>
                  <Typography variant="caption" color="text.secondary" fontWeight="medium">
                    {department}
                  </Typography>
                  <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                    {teams.map((team) => (
                      <Stack
                        key={team.fullName || team.teamName}
                        direction="row"
                        alignItems="center"
                        spacing={1}
                      >
                        <ColorBox color={team.color} />
                        <Typography variant="caption">{team.teamName}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Box>
              ))}
            </LegendGrid>
            {/* 공석 */}
            <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <ColorBox color={EMPTY_SEAT_COLOR} />
                <Typography variant="caption">공석</Typography>
              </Stack>
            </Box>
          </TeamColorLegend>
        )}
      </>

      {/* SVG 도면 */}
      <SvgContainer editMode={editMode}>
        <LayoutSVG
          ref={svgRef}
          style={{ width: '100%', height: 'auto', cursor: editMode ? 'pointer' : 'default' }}
          onClick={handleSvgClick}
        />
      </SvgContainer>
    </MapContainer>
  );
};

export default SeatMap;

const MapContainer = styled(Box)({
  position: 'relative',
  width: '100%',
  overflow: 'auto',
});

const EditModeNotice = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 10,
  left: '50%',
  transform: 'translateX(-50%)',
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(1, 2),
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[2],
  zIndex: 100,
}));

const LegendToggleButton = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: 10,
  right: 10,
  zIndex: 101,
  backgroundColor: theme.palette.background.paper,
  borderRadius: '50%',
  boxShadow: theme.shadows[2],
}));

const TeamColorLegend = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: 30,
  right: 10,
  zIndex: 100,
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[2],
  padding: theme.spacing(1.5),
  maxHeight: 'calc(100% - 20px)',
  overflowY: 'auto',
}));

const LegendGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: theme.spacing(2),
}));

const ColorBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'color',
})<{ color: string }>(({ color }) => ({
  width: 16,
  height: 16,
  borderRadius: 2,
  backgroundColor: color,
  border: '1px solid #ddd',
  flexShrink: 0,
}));

const SvgContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'editMode',
})<{ editMode: boolean }>(({ editMode }) => ({
  '& rect': {
    cursor: editMode ? 'pointer' : 'default',
    transition: 'opacity 0.2s',
    '&:hover': editMode
      ? {
          opacity: 0.7,
        }
      : {},
  },
}));
